import { type GetProductPricesQuery, type ProductPriceTierInput } from '@codezo/graphql';
import { Plus, Trash2 } from 'lucide-react';
import { VetterSelector } from '../../components/selectors/VetterSelector';
import {
	applyPriceEdit,
	belowCost,
	parseAmount,
	pricingPayload,
	type TierEntryMode,
} from '../../components/pricing/tier-pricing';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { CurrencyInput } from '../../components/ui/currency-input';
import { NumberInput } from '../../components/ui/number-input';
import { TierEconomicsReadout } from '../../components/pricing/TierEconomicsReadout';

type PriceRow = GetProductPricesQuery['getProductPrices'][number];

/**
 * One tier being edited. Rows have no stable identity until they are saved, so
 * `key` is local and never submitted.
 */
/** The supplier picker's value for the house ladder — no supplier id exists for it. */
export const HOUSE_LADDER = 'house';

export type TierDraft = {
	key: string;
	/**
	 * Which ladder this tier belongs to: a supplier id, or `HOUSE_LADDER`.
	 *
	 * On the row rather than above the list because that is where the backend
	 * keeps it — `vetterEntityId` is a column on `ProductPrice`, and resolution
	 * happens one quantity at a time. Only the create flow lets it vary; the
	 * edit modal is scoped to one ladder, because saving replaces that ladder
	 * whole.
	 */
	vetterEntityId: string;
	quantity: string;
	baseUnitPrice: string;
	currency: string;
	markupPercentage: string;
	/** What the client would pay per unit. Derived by the backend, but authorable. */
	salePrice: string;
	/** Which of sale price / markup the admin typed. Only that one is sent. */
	entryMode: TierEntryMode;
	minProductionDays: string;
	maxProductionDays: string;
	isActive: boolean;
	/**
	 * The row's validity window, carried through untouched.
	 *
	 * `setProductPrices` REPLACES a ladder, so a field left out of the payload is
	 * not "unchanged" — it is cleared. Editing one tier's cost would otherwise
	 * strip the dates off every row in that ladder.
	 */
	validFrom?: string | null;
	validTo?: string | null;
	/** Present on a row the accepted-offer write-back created. */
	lastQuotedUnitPrice?: number | null;
	lastQuotedCurrency?: string | null;
	/**
	 * The cost as stored, when it is stored in another currency than the one
	 * shown. Said beside the row; never sent.
	 */
	stored?: { baseUnitPrice: number; currency: string } | null;
};

export const newTierDraft = (
	currency: string,
	key: string,
	vetterEntityId: string = HOUSE_LADDER
): TierDraft => ({
	key,
	vetterEntityId,
	quantity: '',
	baseUnitPrice: '',
	currency,
	markupPercentage: '',
	salePrice: '',
	// The catalogue stores a markup, so a cost change holds the markup and moves
	// the price — the usual case being a supplier's cost moving under an
	// unchanged markup policy.
	entryMode: 'markup',
	minProductionDays: '',
	maxProductionDays: '',
	isActive: true,
});

/**
 * Keyed by the row's own id, so a caller can name one tier without knowing its position.
 *
 * Loaded in the header currency from `converted`, like everything else on
 * screen, and sent back in it touched or not: `setProductPrices` replaces the
 * whole ladder, and the backend keeps a cost within half a cent of the stored
 * one exactly as stored. Only a changed cost is re-priced in the header
 * currency. With no rate (`converted` null) the row stays in its own.
 */
export const rowToTierDraft = (row: PriceRow): TierDraft => {
	const fig = row.converted;
	return {
		key: row.id,
		vetterEntityId: row.vetterEntityId ?? HOUSE_LADDER,
		quantity: String(row.quantity),
		baseUnitPrice: String(fig?.baseUnitPrice ?? row.baseUnitPrice),
		currency: fig?.currency ?? row.currency,
		stored:
			fig && fig.currency !== row.currency
				? { baseUnitPrice: row.baseUnitPrice, currency: row.currency }
				: null,
		markupPercentage: row.markupPercentage != null ? String(row.markupPercentage) : '',
		salePrice:
			(fig ? fig.unitSalePrice : row.unitSalePrice) != null
				? String(fig ? fig.unitSalePrice : row.unitSalePrice)
				: '',
		validFrom: row.validFrom ?? null,
		validTo: row.validTo ?? null,
		entryMode: 'markup',
		minProductionDays: row.minProductionDays != null ? String(row.minProductionDays) : '',
		maxProductionDays: row.maxProductionDays != null ? String(row.maxProductionDays) : '',
		isActive: row.isActive,
		lastQuotedUnitPrice: fig ? fig.lastQuotedUnitPrice : row.lastQuotedUnitPrice,
		lastQuotedCurrency: fig ? fig.currency : row.lastQuotedCurrency,
	};
};

/** One tier, checked and converted. Shared so the two paths cannot disagree. */
function tierInput(
	t: TierDraft
): { ok: true; input: ProductPriceTierInput } | { ok: false; error: string } {
	// Same parser as the offer forms: a comma is a decimal point here too.
	const quantity = parseInt(t.quantity.replace(/[^\d-]/g, ''), 10);
	const baseUnitPrice = parseAmount(t.baseUnitPrice) ?? NaN;
	if (!Number.isFinite(quantity) || quantity <= 0) {
		return { ok: false, error: 'Every tier needs a quantity above zero.' };
	}
	if (!Number.isFinite(baseUnitPrice) || baseUnitPrice < 0) {
		return { ok: false, error: `Tier ${quantity.toLocaleString()} needs a cost.` };
	}
	if (t.entryMode === 'sale' && belowCost(t.baseUnitPrice, t.salePrice)) {
		return {
			ok: false,
			error: `Tier ${quantity.toLocaleString()} has a sale price below its cost.`,
		};
	}
	return {
		ok: true,
		input: {
			quantity,
			/* Sale price and markup are mutually exclusive on the input — the
			   backend converts a sale price into a markup, which is the only thing
			   the catalogue stores, so sending the typed one keeps the conversion
			   server-side rather than rounding it here. */
			...pricingPayload({
				cost: t.baseUnitPrice,
				salePrice: t.salePrice,
				markup: t.markupPercentage,
				entryMode: t.entryMode,
				currency: t.currency,
			}),
			minProductionDays: t.minProductionDays ? parseInt(t.minProductionDays, 10) : undefined,
			maxProductionDays: t.maxProductionDays ? parseInt(t.maxProductionDays, 10) : undefined,
			// Resent rather than omitted — see the note on the draft.
			validFrom: t.validFrom ?? undefined,
			validTo: t.validTo ?? undefined,
			isActive: t.isActive,
		},
	};
}

/**
 * Validates drafts into ONE ladder's tier list — the edit modal, which is scoped
 * to a single supplier.
 *
 * Duplicate quantities are caught here rather than at the server, which rejects
 * them outright; here the admin can still see both offending rows.
 */
export function buildTierInputs(
	tiers: TierDraft[]
): { ok: true; tiers: ProductPriceTierInput[] } | { ok: false; error: string } {
	const seen = new Set<number>();
	const out: ProductPriceTierInput[] = [];
	for (const t of tiers) {
		const built = tierInput(t);
		if (!built.ok) return built;
		if (seen.has(built.input.quantity)) {
			return {
				ok: false,
				error: `Two tiers are both set to ${built.input.quantity.toLocaleString()}.`,
			};
		}
		seen.add(built.input.quantity);
		out.push(built.input);
	}
	return { ok: true, tiers: out };
}

/** One ladder's worth of tiers, ready for a single `setProductPrices` call. */
export type TierGroup = { vetterEntityId: string | null; tiers: ProductPriceTierInput[] };

/**
 * Validates drafts that may span SEVERAL ladders, grouping them one per
 * `setProductPrices` call — the create flow, where a new product can be given a
 * house price and a supplier's price in one pass.
 *
 * Duplicate quantities are checked PER LADDER, not across all of them: the same
 * volume priced for the house and for a supplier is two legitimate rows, and it
 * is the pair the whole house/supplier fallback exists to express.
 */
export function buildTierGroups(
	tiers: TierDraft[]
): { ok: true; groups: TierGroup[] } | { ok: false; error: string } {
	const byLadder = new Map<string, { quantities: Set<number>; tiers: ProductPriceTierInput[] }>();
	for (const t of tiers) {
		const built = tierInput(t);
		if (!built.ok) return built;
		const ladder = byLadder.get(t.vetterEntityId) ?? { quantities: new Set(), tiers: [] };
		if (ladder.quantities.has(built.input.quantity)) {
			return {
				ok: false,
				error: `Two tiers on the same ladder are both set to ${built.input.quantity.toLocaleString()}.`,
			};
		}
		ladder.quantities.add(built.input.quantity);
		ladder.tiers.push(built.input);
		byLadder.set(t.vetterEntityId, ladder);
	}
	return {
		ok: true,
		groups: [...byLadder.entries()].map(([vetterEntityId, ladder]) => ({
			vetterEntityId: vetterEntityId === HOUSE_LADDER ? null : vetterEntityId,
			tiers: ladder.tiers,
		})),
	};
}

/**
 * The ladder editor, shared by the product page's Prices tab and the create
 * modal's Prices step.
 *
 * Deliberately one component rather than two: `setProductPrices` replaces a
 * whole ladder, so a second editor that drifted from this one would quietly
 * delete rows it did not know to send.
 */
export function TierListEditor({
	tiers,
	onChange,
	emptyNote,
	currency,
	supplierPicker = false,
	allowAdd = true,
}: {
	tiers: TierDraft[];
	onChange: (next: TierDraft[]) => void;
	/** What an empty ladder means in this context. */
	emptyNote: string;
	/** Currency a newly added tier starts in. */
	currency: string;
	/**
	 * Let each tier choose its own ladder. Create-flow only: the edit modal is
	 * scoped to one supplier because saving REPLACES that supplier's ladder, so a
	 * tier moved to another ladder there would delete itself from the first.
	 */
	supplierPicker?: boolean;
	/** Off when the editor is scoped to a single tier, where adding contradicts it. */
	allowAdd?: boolean;
}) {
	const update = <K extends keyof TierDraft>(key: string, field: K, value: TierDraft[K]) =>
		onChange(tiers.map((t) => (t.key === key ? { ...t, [field]: value } : t)));

	/* Cost, sale price and markup move together — the same helper the offer forms
	   use, mapped onto this row's field names. */
	const updatePrice = (key: string, field: 'cost' | 'salePrice' | 'markup', value: string) =>
		onChange(
			tiers.map((t) => {
				if (t.key !== key) return t;
				const synced = applyPriceEdit(
					{
						cost: t.baseUnitPrice,
						salePrice: t.salePrice,
						markup: t.markupPercentage,
						entryMode: t.entryMode,
					},
					field,
					value
				);
				return {
					...t,
					baseUnitPrice: synced.cost,
					salePrice: synced.salePrice,
					markupPercentage: synced.markup,
					entryMode: synced.entryMode,
				};
			})
		);

	/* A new tier inherits the last one's supplier: several tiers for one supplier
	   is the normal shape of a ladder, and it saves setting it on every row
	   without needing a control of its own above the list. */
	const add = () =>
		onChange([
			...tiers,
			newTierDraft(
				currency,
				`new-${Date.now()}-${tiers.length}`,
				tiers[tiers.length - 1]?.vetterEntityId ?? HOUSE_LADDER
			),
		]);

	return (
		<div className="flex flex-col">
			<div className="flex flex-col gap-3">
				{tiers.map((t) => (
					<TierEditor
						key={t.key}
						tier={t}
						supplierPicker={supplierPicker}
						update={update}
						updatePrice={updatePrice}
						onRemove={() => onChange(tiers.filter((x) => x.key !== t.key))}
					/>
				))}
				{tiers.length === 0 && (
					<p className="py-4 text-center text-sm text-text-4">{emptyNote}</p>
				)}
			</div>

			{allowAdd && (
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="mt-3 self-start"
					onClick={add}
				>
					<Plus size={14} data-icon="inline-start" />
					Add tier
				</Button>
			)}
		</div>
	);
}

/** One tier's fields. */
function TierEditor({
	tier,
	supplierPicker,
	update,
	updatePrice,
	onRemove,
}: {
	tier: TierDraft;
	supplierPicker: boolean;
	update: <K extends keyof TierDraft>(key: string, field: K, value: TierDraft[K]) => void;
	updatePrice: (key: string, field: 'cost' | 'salePrice' | 'markup', value: string) => void;
	onRemove: () => void;
}) {
	// The write-back creates a row from an accepted offer with no authored cost.
	// It carries evidence and nothing else, so say that rather than show its zero.
	const neverAuthored =
		!tier.isActive && tier.baseUnitPrice === '0' && tier.lastQuotedUnitPrice != null;

	return (
		<div className="rounded-lg border border-line-1 p-3">
			<div className="mb-2 flex items-center justify-between">
				<span className="text-xs font-medium text-text-3">
					{tier.quantity ? `${Number(tier.quantity).toLocaleString()} pcs` : 'New tier'}
				</span>
				<button
					type="button"
					onClick={onRemove}
					title="Remove this tier"
					className="rounded p-1 text-text-4 transition-colors hover:text-destructive"
				>
					<Trash2 size={14} />
				</button>
			</div>

			{tier.stored && (
				<p className="mb-2 text-2xs text-text-4">
					Stored as {tier.stored.baseUnitPrice.toLocaleString()} {tier.stored.currency}.
					Saving keeps it that way unless you change the cost, which stores it in{' '}
					{tier.currency}.
				</p>
			)}

			{neverAuthored && (
				<p className="mb-2 text-2xs text-text-4">
					Quoted, never authored — last quoted at{' '}
					{tier.lastQuotedUnitPrice?.toLocaleString()} {tier.lastQuotedCurrency}. Setting
					a cost here keeps that history.
				</p>
			)}

			{supplierPicker && (
				<div className="mb-2 flex flex-col gap-1">
					<span className="text-2xs text-text-4">Prices for</span>
					<VetterSelector
						value={
							tier.vetterEntityId === HOUSE_LADDER
								? HOUSE_LADDER
								: tier.vetterEntityId
						}
						onChange={(v) => update(tier.key, 'vetterEntityId', v || HOUSE_LADDER)}
						extraOptions={[{ value: HOUSE_LADDER, label: 'No supplier' }]}
						placeholder="No supplier"
						selectedLabel={
							tier.vetterEntityId === HOUSE_LADDER ? 'No supplier' : undefined
						}
						creatable
					/>
				</div>
			)}

			<div className="grid grid-cols-2 gap-2">
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Quantity</span>
					<NumberInput
						value={tier.quantity}
						onChange={(e) =>
							update(tier.key, 'quantity', (e.target as HTMLInputElement).value)
						}
						placeholder="e.g. 500"
						min={1}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Unit cost</span>
					<CurrencyInput
						currency={tier.currency}
						value={tier.baseUnitPrice}
						onChange={(e) =>
							updatePrice(tier.key, 'cost', (e.target as HTMLInputElement).value)
						}
						placeholder="0.00"
						min={0}
						step="any"
					/>
				</div>
				<div className="flex flex-col gap-1">
					{/* Authorable, though the catalogue stores only the markup: the input
					    takes a sale price and the backend converts it, so the rounding
					    happens there rather than here. Read back it is derived again. */}
					<span className="text-2xs text-text-4">Sale price</span>
					<CurrencyInput
						currency={tier.currency}
						value={tier.salePrice}
						onChange={(e) =>
							updatePrice(tier.key, 'salePrice', (e.target as HTMLInputElement).value)
						}
						placeholder="0.00"
						min={0}
						step="any"
					/>
					{tier.entryMode === 'sale' && belowCost(tier.baseUnitPrice, tier.salePrice) && (
						<span className="text-2xs text-destructive">
							Below cost — this will be rejected.
						</span>
					)}
				</div>
				<div className="flex flex-col gap-1">
					{/* Markup, not margin: profit over COST, which is what an admin
					    types when pricing. Margin — the same profit over REVENUE — is a
					    different number and keeps its own name on the reporting screens.
					    Deliberately not "avance": e-conomic uses that word for the
					    revenue-based figure, so it would read as the other number to
					    anyone with both tools open. The offer pricing form says Markup
					    for the same field. */}
					<span
						className="text-2xs text-text-4"
						title="Profit over cost: price = cost × (1 + markup/100). The same figure as the Markup on an offer tier — not the margin over revenue shown on an order summary."
					>
						Markup %
					</span>
					<NumberInput
						value={tier.markupPercentage}
						onChange={(e) =>
							updatePrice(tier.key, 'markup', (e.target as HTMLInputElement).value)
						}
						placeholder="e.g. 20"
						min={0}
						step="any"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Min production days</span>
					<NumberInput
						value={tier.minProductionDays}
						onChange={(e) =>
							update(
								tier.key,
								'minProductionDays',
								(e.target as HTMLInputElement).value
							)
						}
						placeholder="—"
						min={0}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Max production days</span>
					<NumberInput
						value={tier.maxProductionDays}
						onChange={(e) =>
							update(
								tier.key,
								'maxProductionDays',
								(e.target as HTMLInputElement).value
							)
						}
						placeholder="—"
						min={0}
					/>
				</div>
			</div>

			{/*
			 * The same breakdown the offer pricing form shows, so a catalogue price
			 * and the offer it prefills are read the same way.
			 *
			 * The catalogue authors a MARKUP, not a sale price — `ProductPriceTierInput`
			 * has no sale-price field — so the price is derived from cost and markup
			 * rather than typed. What it previews is what this row would quote at.
			 */}
			<TierEconomicsReadout
				cost={tier.baseUnitPrice}
				salePrice={tier.salePrice}
				quantity={parseInt(tier.quantity, 10) || 0}
				currency={tier.currency}
				className="mt-2"
				costOnlyMessage="No markup — this tier stores a cost only, and prefills an offer with no markup."
				emptyMessage="Enter a cost and a markup"
			/>

			<label className="mt-2 flex cursor-pointer items-center gap-2">
				<Checkbox
					checked={tier.isActive}
					onCheckedChange={(v) => update(tier.key, 'isActive', v === true)}
				/>
				<span className="text-2xs text-text-3">
					Active — an inactive tier is left out of pricing and prefill
				</span>
			</label>
		</div>
	);
}
